import { useEffect, useState } from "react"
import { ReactComponent as DuplicateIcon } from "../assets/images/icons/ic-copy.svg"
import { ReactComponent as DeleteIcon } from "../assets/images/icons/ic-delete.svg"
import { ReactComponent as EditIcon } from "../assets/images/icons/ic-pencil.svg"
import { FeedbackQuestionType, FeedbackType } from "../common/constants"
import { Each } from '../common/Each'
import useContextMenu from "../common/hooks/useContextMenu"
import { includesCaseInsensitive } from "../utils"
import StatusBadge from "./StatusBadge"
import MeatBallsMenu from "./MeatBallsMenu"
import Card from "./cards/Card"
import Dialog from "./dialogs/Dialog"
import FeedbackViewer from "./dialogs/FeedbackViewer"
import styles from "./FeedbacksCourses.module.css"
import RoundBadge from "./RoundBadge"
import Table from "./Table"
import TextInput from "./TextInput"


const FeedbacksCourses = ({ feedbacks = [], feedbacksLoading = true, onEdit = () => { }, onDelete = () => { }, onDuplicate = () => { } }) => {
	const feedbackTypes = [{ id: FeedbackType.Course, label: "Corsi" }, { id: FeedbackType.Lesson, label: "Lezioni" }]


	const { showMenu } = useContextMenu()

	const [filter] = useState(feedbackTypes[0])
	const [filteredFeedbacks, setFilteredFeedbacks] = useState([])
	const [students, setStudents] = useState([])
	const [filteredStudents, setFilteredStudents] = useState([])
	const [, setLoading] = useState(true)
	const [openViewerDialog, setOpenViewerDialog] = useState(false)

	const [selectedFeedback, setSelectedFeedback] = useState(null)
	const [selectedEdition, setSelectedEdition] = useState(null)
	const [selectedUserFeedback, setSelectedUserFeedback] = useState(null)

	const [editionSearch, setEditionSearch] = useState("")

	useEffect(() => {
		setLoading(true)
		const newFeedbacks = []
		for (const feedback of feedbacks) {
			let sum = 0
			let count = 0
			for (const user_feedback of feedback.users_feedback) { // somma tutte le risposte numeriche di tutti gli studenti
				const answers = user_feedback.content.filter(c => [FeedbackQuestionType.Qualitative, FeedbackQuestionType.Quantitative].includes(c.type)).map(q => q.answer)
				sum += answers.reduce((a, c) => a += c, 0)
				count += answers.length
			}
			feedback.average = count > 0 ? Math.round(sum / count * 10) / 10 : 0

			for (const edition of feedback.course.editions) {
				const studentsWithFeedback = feedback.users_feedback.filter(uf => uf.resource_type === FeedbackType.Edition && uf.resource_id === edition.id)
				const editionsStudents = edition.students

				let students = []
				for (const student of editionsStudents) {
					const studentWithFeedback = studentsWithFeedback.find(s => s.user.id === student.id)
					student.average = null
					if (studentWithFeedback) {
						const numericQuestions = studentWithFeedback.content.filter(c => [FeedbackQuestionType.Qualitative, FeedbackQuestionType.Quantitative].includes(c.type))
						student.average = average(numericQuestions.map(q => q.answer))
						student.feedback = studentWithFeedback
					}
					students.push(student)
				}
				edition.students = students.sort((a, b) => b.average - a.average)
				edition.average = average(edition.students.map(s => s.average))
				edition.feedbackCounter = studentsWithFeedback.length
				edition.content = structuredClone(feedback.content.filter(q => [FeedbackQuestionType.Qualitative, FeedbackQuestionType.Quantitative].includes(q.type)).map(q => ({ ...q, answers: [] })))

				for (const student of studentsWithFeedback) {
					for (const numericQuestion of student.content.filter(q => [FeedbackQuestionType.Qualitative, FeedbackQuestionType.Quantitative].includes(q.type))) {
						const editionQuestion = edition.content.find(q => q.id === numericQuestion.id)
						const data = { id: numericQuestion.id, question: numericQuestion.question, answers: [numericQuestion.answer] }
						if (!editionQuestion) {
							edition.content.push(data)
						} else {
							editionQuestion.answers.push(numericQuestion.answer)
						}
					}
				}
				if (edition.content.length > 0) {
					edition.content = edition.content.map(q => ({ ...q, average: average(q.answers) }))
					for (const question of edition.content) { // calcola percentuale per ogni risposta di ogni domanda
						const occurences = new Array(5).fill(0)
						let sum = 0
						for (const a of [1, 2, 3, 4, 5]) {
							occurences[a - 1] = question.answers.reduce((acc, c) => c === a ? acc + 1 : acc, 0)
							sum += occurences[a - 1]
						}
						question.percentages = occurences.map(o => sum ? Math.round((o / sum) * 100) : 0)
					}
				}

				edition.startAt = getEditionStart(edition) ?? ""
			}

			feedback.course.editions.sort((a, b) => new Date(b.startAt) - new Date(a.startAt))
			newFeedbacks.push(feedback)
		}
		setFilteredFeedbacks(newFeedbacks)
		setLoading(false)
	}, [filter, feedbacks])

	useEffect(() => {
		if (!selectedEdition) return

		const editionsStudents = selectedEdition.students
		setStudents(editionsStudents)
		setFilteredStudents(editionsStudents)
	}, [selectedEdition, selectedFeedback])

	const onContextMenu = (e, feedback) => {
		const actions = [
			{ name: "Duplica", icon: <DuplicateIcon className={styles.icon} style={{ color: "var(--sf-gray)" }} />, onClick: () => onDuplicate(feedback) },
			{ name: "Modifica", icon: <EditIcon className={styles.icon} style={{ color: "var(--sf-light-green)" }} />, onClick: () => onEdit(feedback) },
			{ name: "Elimina", icon: <DeleteIcon className={styles.icon} style={{ color: "var(--sf-red)" }} />, onClick: () => onDelete(feedback) },
		]
		showMenu(e, actions)
	}

	const average = (arr) => {
		arr = arr.filter(a => a > 0)
		return arr.length > 0 ? Math.round(arr.reduce((a, c) => a += c, 0) / arr.length * 10) / 10 : 0
	}

	const getBadgeColor = (s) => {
		switch (s) {
			case "hidden": return "var(--sf-red)";
			case "draft": return "var(--sf-purple)";
			case "public": return "var(--sf-light-green)";
			default: return "black";
		}
	}

	const getAverageColor = (avg) => {
		if (!avg)
			return "var(--sf-gray)"

		if (avg < 2.5) {
			return "var(--sf-red)"
		}
		if (avg < 4) {
			return "var(--sf-yellow)"
		}
		if (avg >= 4)
			return "var(--sf-green)"
	}

	const getEditionStart = (edition) => {
		let lessons = edition.modules.flatMap(module => module.lessons || [])
		// Filtra le lezioni che non hanno starts_at definito
		lessons = lessons.filter(lesson => lesson.starts_at);

		if (lessons.length === 0) return undefined; // Se non ci sono lezioni valide, ritorna undefined

		const objectWithMinStartAt = lessons.reduce((min, current) => {
			return new Date(current.starts_at) < new Date(min.starts_at) ? current : min;
		}, lessons[0]);

		return objectWithMinStartAt?.starts_at;
	}

	const handleCourseClick = (feedback) => {
		if (selectedFeedback?.id === feedback.id) {
			setSelectedFeedback(null)
			setSelectedEdition(null)
		} else {
			setSelectedFeedback(feedback)
			setSelectedEdition(feedback.course.editions[0] ?? null)
		}
	}

	const onSearch = (text) => {
        setFilteredStudents(students.filter(student => `${student.name} ${student.surname}`.toLowerCase().includes(text.trim().toLowerCase())))
    }

	const getHeight = (question, answer) => {
		return `${(question.percentages[answer - 1] || 0) * 40 / 100}px`
	}

	return (
		<>
			<div className={styles.list}><Card appearance="modern" integratedTable title="Feedback corsi">
				<Table appearance="minimal"
					onRowClick={r => handleCourseClick(feedbacks.find(f => f.id === r.id))}
					loading={feedbacksLoading}
					loadingN={5}
					style={{ borderRadius: 0, border: 0, marginTop: 0 }}
					onContextMenu={onContextMenu}
					columns={[
						{ key: 'name', label: 'NOME', type: "string" },
						{ key: 'course', label: 'CORSO', type: "string" },
						{ key: 'feedbacks', label: 'N. FEEDBACK', type: "number" },
						{ key: '_average', label: 'MEDIA VOTI', type: "custom" },
						{ key: '_status', label: 'STATO', type: 'custom' },
                        { key: 'actions', label: 'Azioni', type: 'custom', handleClick: true },
					]}
					elements={filteredFeedbacks.map(feedback => ({
						...feedback,
                        actions: <MeatBallsMenu actions={[
                            { label: 'Modifica', icon: EditIcon, onClick: () => onEdit(feedback) },
                            { label: 'Duplica', icon: DuplicateIcon, onClick: () => onDuplicate(feedback) },
                            { label: 'Elimina', icon: DeleteIcon, onClick: () => onDelete(feedback), style: { color: 'var(--sf-red)' } },
                        ]} />,
                        course: feedback.course?.name,
						feedbacks: feedback.users_feedback?.length ?? 0,
						_status: { value: feedback.status, type: 'string', component: <StatusBadge appearance="soft" color={getBadgeColor(feedback.status)} label={{ public: "Pubblico", draft: "Bozza", hidden: "Nascosto" }[feedback.status] || feedback.status} /> },
						_average: {
							value: feedback.average, type: 'number', component: <span onClick={() => {
								setSelectedFeedback(feedback)
								setSelectedEdition(feedback.course.editions[0] ?? null)
							}} style={{ color: getAverageColor(feedback.average), fontWeight: 600 }}>{feedback.average}</span>
						},
					}))}
				/>
			</Card></div>
			{selectedFeedback &&
				<Dialog appearance="modern"
					open={!!selectedFeedback}
					title={selectedFeedback.name}
					onClose={() => {
						setSelectedFeedback(null)
                        setOpenViewerDialog(false)
						setSelectedEdition(null)
						setSelectedUserFeedback(null)
					}}
					style={{ width: "min(1180px, 94vw)" }}>
					<div className={styles.editionsWrapper}>
						<div className={styles.editions}><h3 className={styles.panelTitle}>Edizioni e partecipanti</h3>
							<div style={{ padding: "8px", width: '100%' }}>
								<TextInput appearance="modern" type="search" onKeyUp={setEditionSearch} placeholder={"Cerca edizione"} />
							</div>
							<Table appearance="minimal"
								selectedElementID={selectedEdition?.id}
								onRowClick={e => setSelectedEdition(e)}
								style={{ borderRadius: 0, maxHeight: '230px', marginTop: 0, border: 0 }}
								columns={[
									{ key: 'name', label: 'NOME', type: "string" },
									{ key: 'feedbacks', label: 'N. FEEDBACK', type: "number", style: { justifyContent: 'center' } },
									{ key: '_average', label: 'MEDIA VOTI', type: "custom", style: { justifyContent: 'center' } },
								]}
								elements={selectedFeedback.course.editions
									.filter(edition => editionSearch.trim().length > 0 ? includesCaseInsensitive(edition.name, editionSearch) : true)
									.map(edition => ({
									...edition,
									feedbacks: edition.feedbackCounter ?? 0,
									_average: {
										value: edition.average, type: 'number',
										component: <span onClick={() => {
											setSelectedEdition(edition)
										}} style={{ color: getAverageColor(edition.average), fontWeight: 600, display: 'flex', justifyContent: 'center' }}>{edition.average}</span>
									},
								}))}
							/>
							<div style={{ padding: "8px", width: '100%' }}>
								<TextInput appearance="modern" type="search" onKeyUp={onSearch} placeholder={"Cerca studente"} />
							</div>
							<Table appearance="minimal"
								onRowClick={(element) => {
									setSelectedUserFeedback(element)
									setOpenViewerDialog(true)
								}}
								style={{ borderRadius: 0, marginTop: 0 }}
								columns={[
									{ key: 'name', label: 'NOME', type: "string" },
									{ key: 'surname', label: 'COGNOME', type: "string" },
									{ key: '_average', label: 'MEDIA VOTI', type: "custom", style: { justifyContent: 'center' } }
								]}
								elements={filteredStudents.filter(s => !!s.feedback).map(userFeedback => ({
									...userFeedback,
									_average: {
										value: userFeedback.average, type: 'number', component: <span onClick={() => {
											setSelectedUserFeedback(userFeedback)
											setOpenViewerDialog(true)
										}} style={{ color: getAverageColor(userFeedback.average), fontWeight: 600, display: 'flex', justifyContent: 'center' }}>{userFeedback.average ?? "-"}</span>
									},
								}))}
							/>
						</div>
						<div className={styles.studentsContainer}>
							{
								selectedEdition &&
								<div className={styles.questions}>
									<div className={styles.editionName}>{selectedEdition.name}</div>
									<Each
										of={selectedEdition?.content ?? []}
										render={(item, index) => (
											<div className={styles.questionContainer}>
												<div className={styles.question}>{index + 1}. {item.question}</div>
												<div className={styles.answers}>
													<Each
														of={[1, 2, 3, 4, 5]}
														render={(answer) => (
															<div className={styles.answer} >
																<div className={styles.answerBackground} style={{ height: getHeight(item, answer) }} />
																<div className={styles.answerValue}>{answer}</div>
															</div>
														)}
													/>
												</div>
												<RoundBadge color={getAverageColor(item.average)}>{item.average}</RoundBadge>
											</div>
										)}
									/>
								</div>
							}
						</div>
					</div>
				</Dialog>
			}
			<Dialog appearance="modern" style={{ width: "min(760px, 94vw)" }}
				open={openViewerDialog}
				onClose={() => {
					setOpenViewerDialog(false)
					setSelectedUserFeedback(null)
				}}
				title={selectedUserFeedback ? `${selectedUserFeedback.name} ${selectedUserFeedback.surname}` : "Risposte del partecipante"}
				contentMaxHeight="80dvh"
				action={
					<div className={styles.dialogAction}>
					</div>
				}
			>
				<FeedbackViewer userFeedback={selectedUserFeedback} feedback={selectedFeedback} />
			</Dialog>
		</>
	)
}

export default FeedbacksCourses
